import type { HeaderProps } from './Header.types';

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import * as selectors from 'features/sessions/selectors';
import { SessionSectionType } from '../../Session.constants';
import { TimerButton, CounterButton } from '../Buttons';
import { useParams } from 'react-router-dom';
import './_header.scss';

export const Header = ({ onTimeExpired, isSessionCompleted, sectionType, packageInstructionIndex }: HeaderProps) => {
  const currentPackage = useSelector(selectors.selectCurrentPackage);
  const currentSession = useSelector(selectors.selectCurrentSession);
  const currentQuestionIndex = useSelector(selectors.selectCurrentQuestionIndex);
  const currentQuestionResponse = useSelector(selectors.selectCurrentQuestionResponse);
  const { type } = useParams();
  const isUnlimited: boolean = type ? atob(type) === 'unlimited' : false;
  const timerButton = useMemo(() => {
    if (isSessionCompleted) {
      return null;
    }

    if (sectionType === SessionSectionType.PACKAGE_INSTRUCTION) {
      return packageInstructionIndex > 1 ? (
        <TimerButton id={uuid()} duration={120} warning={120} onTimeExpired={onTimeExpired} unlimited={isUnlimited} />
      ) : null;
    }

    if (sectionType === SessionSectionType.SECTION_INSTRUCTION) {
      return <TimerButton id={uuid()} duration={60} warning={60} onTimeExpired={onTimeExpired} unlimited={isUnlimited}/>;
    }

    if (
      sectionType === SessionSectionType.QUESTION ||
      sectionType === SessionSectionType.REVIEW_PRESCORE
    ) {
      // The clock keeps running on the Review Prescore summary too.
      return <TimerButton duration={currentSession.remaining_time} onTimeExpired={onTimeExpired} unlimited={isUnlimited}/>;
    }

    return null;
  }, [isSessionCompleted, sectionType, packageInstructionIndex, currentSession.remaining_time, onTimeExpired, isUnlimited]);

  const counterButton = useMemo(() => {
    if (isSessionCompleted) {
      return null;
    }

    if (sectionType === SessionSectionType.QUESTION && currentQuestionIndex) {
      return <CounterButton currentIndex={currentQuestionIndex.current + 1} totalCount={currentQuestionIndex.total} />;
    }

    return null;
  }, [isSessionCompleted, sectionType, currentQuestionIndex]);

  const flaggedBadge = useMemo(() => {
    if (!isSessionCompleted || sectionType !== SessionSectionType.QUESTION) {
      return null;
    }
    const isFlagged = Boolean(currentQuestionResponse?.flagged);
    return (
      <span className={`header__flagged-badge${isFlagged ? ' header__flagged-badge--flagged' : ' header__flagged-badge--not-flagged'}`}>
        {isFlagged ? 'Flagged' : 'Not Flagged'}
      </span>
    );
  }, [isSessionCompleted, sectionType, currentQuestionResponse?.flagged]);

  return (
    <>
      <div className="header__container">
        <h3 className="header__title">{currentPackage?.name}</h3>

        <div className="header__buttons">
          {timerButton}
          {counterButton}
          {flaggedBadge}
        </div>
      </div>
    </>
  );
};
